/**
 * Client-side half of the "start searching for a real human" gate. Every
 * search the bar starts on its own goes through it: the not-found recovery
 * view (the dead URL's words) and the search page's `?q=…&autoSearch=true`
 * (the extension, other stores' search links turned into searches by the
 * middleware, and the SearchAction we publish).
 *
 * The server already decided this request LOOKS human (user-agent class) and
 * asked the bar to submit the query on mount. This is the last line of
 * defence in the browser, and it exists for two reasons:
 *
 *  1. Cost. An auto-submit is a Gemini turn plus a page search and, when
 *     nothing matches, a page generation. Headless scrapers with a Chrome UA
 *     execute JavaScript, so a UA check alone would let every one of them
 *     spend a generation on every link they crawl.
 *  2. Navigation. The search auto-opens a confident match with a full
 *     document navigation. If Back (or a reload) re-ran the search, it would
 *     re-open the same match and the Back button would stop working. The
 *     search bar already refuses to auto-redirect a RESTORED turn for exactly
 *     this reason; this gate closes the same loop one level up.
 *
 * Pure where it can be: `decideAutoSearch` and `autoSearchKey` take their
 * inputs as arguments, and `startGatedAutoSearch` takes its environment as
 * optional arguments, so all three unit-test without a DOM.
 */

export type AutoSearchEnv = {
  /** `navigator.webdriver` — true under Puppeteer/Playwright/Selenium. */
  webdriver: boolean;
  /** `document.visibilityState` — a prerendered or background tab must wait. */
  visibilityState: string;
  /** `performance.getEntriesByType('navigation')[0]?.type`. */
  navigationType: string | undefined;
  /** Whether this session already auto-searched for this exact URL. */
  alreadyRan: boolean;
};

export type AutoSearchDecision = 'run' | 'wait-for-visible' | 'skip';

export const decideAutoSearch = (env: AutoSearchEnv): AutoSearchDecision => {
  if (env.webdriver) return 'skip';
  if (env.alreadyRan) return 'skip';
  // Back/forward onto the page: the visitor is retreating from wherever the
  // previous run sent them. Never re-fire — see the module comment.
  if (env.navigationType === 'back_forward') return 'skip';
  // A reload (or a browser restoring its tabs) of a search that already ran
  // never runs it again, even when this session's memory of it is gone
  // (storage blocked or cleared).
  if (env.navigationType === 'reload') return 'skip';
  if (env.visibilityState !== 'visible') return 'wait-for-visible';
  return 'run';
};

const STORAGE_PREFIX = 'iai:404-autosearch:';

/**
 * The URL an auto-search is remembered under: path and query string, so
 * `/en-CA?q=drill` and `/en-CA?q=kayak` are two searches. The `autoSearch`
 * flag itself is left out: the bar removes it from the address bar, and a
 * second look at the same URL must find the same key.
 */
export const autoSearchKey = (location: { pathname: string; search: string }): string => {
  const params = new URLSearchParams(location.search);
  params.delete('autoSearch');
  const search = params.toString();
  return search ? `${location.pathname}?${search}` : location.pathname;
};

/** sessionStorage key for the "already ran on this URL" memory. */
export const autoSearchStorageKey = (key: string): string => `${STORAGE_PREFIX}${key}`;

/** Reads the browser environment into an AutoSearchEnv. Browser-only. */
export const readAutoSearchEnv = (key: string): AutoSearchEnv => {
  let alreadyRan = false;
  try {
    alreadyRan = window.sessionStorage.getItem(autoSearchStorageKey(key)) === '1';
  } catch {
    // Storage can be unavailable (privacy mode / quota); treat as not run.
  }
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  return {
    webdriver: Boolean((navigator as Navigator & { webdriver?: boolean }).webdriver),
    visibilityState: document.visibilityState,
    navigationType: nav?.type,
    alreadyRan,
  };
};

/** Remembers that this URL auto-searched once in this session. Browser-only. */
export const markAutoSearchRan = (key: string): void => {
  try {
    window.sessionStorage.setItem(autoSearchStorageKey(key), '1');
  } catch {
    // Best effort — without storage the back_forward and reload checks still hold.
  }
};

type GateDocument = Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'>;

/**
 * Runs `fire` at most once for the URL `key` when the gate allows it: now, or
 * the moment a background tab is looked at. Returns the effect's cleanup.
 * Both of the bar's auto-runs call this, so they are gated the same way.
 */
export const startGatedAutoSearch = (
  key: string,
  fire: () => void,
  {
    readEnv = readAutoSearchEnv,
    markRan = markAutoSearchRan,
    doc = typeof document === 'undefined' ? undefined : document,
  }: {
    readEnv?: (key: string) => AutoSearchEnv;
    markRan?: (key: string) => void;
    doc?: GateDocument;
  } = {},
): (() => void) => {
  let fired = false;
  const run = () => {
    if (fired) return;
    fired = true;
    markRan(key);
    fire();
  };

  const decision = decideAutoSearch(readEnv(key));
  if (decision === 'skip') return () => undefined;
  if (decision === 'run') {
    run();
    return () => undefined;
  }
  if (!doc) return () => undefined;
  // Background/prerendered tab: start the moment it is actually looked at.
  const onVisible = () => {
    if (doc.visibilityState !== 'visible') return;
    doc.removeEventListener('visibilitychange', onVisible);
    run();
  };
  doc.addEventListener('visibilitychange', onVisible);
  return () => doc.removeEventListener('visibilitychange', onVisible);
};
